"use client";

import { useState, useEffect } from "react";
import { useTranslations, useLocale } from "next-intl";
import { useSearchParams } from "next/navigation";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "motion/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function GiftComponent() {
    const t = useTranslations();
    const locale = useLocale();
    const searchParams = useSearchParams();

    const [step, setStep] = useState(1);
    const [quantity, setQuantity] = useState(1);
    const [recipientEmail, setRecipientEmail] = useState("");
    const [recipientFirstName, setRecipientFirstName] = useState("");
    const [message, setMessage] = useState("");
    const [buyerName, setBuyerName] = useState("");
    const [buyerEmail, setBuyerEmail] = useState("");

    const [unitPrice, setUnitPrice] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchPrice = async () => {
            try {
                const response = await fetch("/api/gift/price");
                const data = await response.json();
                if (data.success) setUnitPrice(data.price);
            } catch (error) {
                setUnitPrice(99);
            }
        };
        fetchPrice();
    }, []);

    useEffect(() => {
        if (searchParams.get("cancelled") === "true") {
            toast.info(t("gift_payment_cancelled"));
        }
    }, [searchParams, t]);

    const validateStep = (currentStep) => {
        if (currentStep === 3) {
            if (!buyerEmail || !buyerName) {
                toast.error(t("gift_buyer_email_required"));
                return false;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail)) {
                toast.error(t("gift_buyer_email_required"));
                return false;
            }
        }
        return true;
    };

    const nextStep = () => {
        if (!validateStep(step)) return;
        setStep(step + 1);
    };

    const prevStep = () => {
        if (step > 1) setStep(step - 1);
    };

    const handlePayment = async () => {
        if (!validateStep(3)) return;

        setLoading(true);
        try {
            const response = await fetch("/api/gift/create-payment", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    quantity,
                    buyerEmail,
                    buyerName,
                    recipientEmail: quantity === 1 ? recipientEmail : null,
                    recipientFirstName: quantity === 1 ? recipientFirstName : null,
                    message: quantity === 1 ? message : null,
                    locale,
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                toast.error(data.message || t("gift_error_generic"));
                setLoading(false);
                return;
            }

            if (data.sessionUrl) {
                window.location.href = data.sessionUrl;
            } else {
                toast.error(t("gift_error_generic"));
                setLoading(false);
            }
        } catch (error) {
            toast.error(t("gift_error_generic"));
            setLoading(false);
        }
    };

    const totalPrice = unitPrice * quantity;

    const GiftIcon = ({ className }) => (
        <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24">
            <path fill="currentColor" d="M2 12c0-4.714 0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22s-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12" opacity=".5" />
            <path fill="currentColor" fillRule="evenodd" d="M6.914 11.25H2v1.5h8.163A3.25 3.25 0 0 1 7 15.25a.75.75 0 0 0 0 1.5a4.75 4.75 0 0 0 4.25-2.626V22h1.5v-7.876A4.75 4.75 0 0 0 17 16.75a.75.75 0 0 0 0-1.5a3.25 3.25 0 0 1-3.163-2.5H22v-1.5h-4.913c.35-.438.613-.955.756-1.527c.538-2.153-1.413-4.103-3.565-3.565a4 4 0 0 0-1.528.756V2h-1.5v4.914a4 4 0 0 0-1.527-.756C7.57 5.62 5.62 7.57 6.158 9.723c.143.572.405 1.089.756 1.527m4.336 0H9.997a2.5 2.5 0 0 1-2.384-1.891A1.44 1.44 0 0 1 9.36 7.613a2.5 2.5 0 0 1 1.891 2.384zm2.753 0H12.75v-1.245a2.5 2.5 0 0 1 1.891-2.392a1.44 1.44 0 0 1 1.746 1.746a2.5 2.5 0 0 1-2.384 1.891" clipRule="evenodd" />
        </svg>
    );

    return (
        <div className="min-h-screen bg-white dark:bg-bgDark-700">
            <div className="max-w-lg mx-auto px-6 py-16 sm:py-24">
                {/* Header - Always visible */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-12"
                >
                    {/* Gift card illustration */}
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="relative w-64 h-40 mx-auto mb-8"
                    >
                        <div className="absolute inset-0 bg-purple-500/20 rounded-2xl blur-xl transform translate-y-2" />
                        <div className="relative w-full h-full bg-gradient-to-br from-purple-600 via-purple-500 to-violet-600 rounded-2xl shadow-xl p-5 flex flex-col justify-between overflow-hidden">
                            <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full" />
                            <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-white/10 rounded-full" />
                            <div className="relative flex justify-between items-start">
                                <GiftIcon className="w-8 h-8 text-white/90" />
                                <span className="text-white/70 text-xs font-medium">1 {t("gift_year")}</span>
                            </div>
                            <div className="relative">
                                <p className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5">{t("gift_card_label")}</p>
                                <p className="text-white text-base font-semibold mb-0">Believemy Premium</p>
                            </div>
                        </div>
                    </motion.div>

                    <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 dark:text-white tracking-tight mb-3">
                        {t("gift_title")}
                    </h1>
                    <p className="text-gray-500 dark:text-grayDark-400 text-base">
                        {t("gift_subtitle")}
                    </p>
                </motion.div>

                {/* Form */}
                <AnimatePresence mode="wait">
                    {/* Step 1: Quantity */}
                    {step === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="space-y-8">
                                {/* Quantity pills */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-4 text-center">
                                        {t("gift_quantity_title")}
                                    </label>
                                    <div className="flex flex-wrap justify-center gap-2">
                                        {[1, 2, 3, 5, 10].map((q) => (
                                            <button
                                                key={q}
                                                onClick={() => setQuantity(q)}
                                                className={`min-w-[72px] px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                                                    quantity === q
                                                        ? "bg-gray-900 dark:bg-white text-white dark:text-grayDark-900"
                                                        : "bg-gray-100 dark:bg-grayDark-800 text-gray-600 dark:text-grayDark-400 hover:bg-gray-200 dark:hover:bg-grayDark-700"
                                                }`}
                                            >
                                                {q}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex items-center justify-center gap-3 mt-4">
                                        <span className="text-xs text-gray-400">{t("gift_or_custom")}</span>
                                        <input
                                            type="number"
                                            value={quantity}
                                            onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                                            className="w-16 text-center px-2 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-grayDark-700 bg-transparent focus:outline-hidden focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
                                            min={1}
                                            max={100}
                                        />
                                    </div>
                                </div>

                                {/* Price */}
                                <div className="text-center py-6 border-y border-gray-100 dark:border-grayDark-800">
                                    <p className="text-5xl font-semibold text-gray-900 dark:text-white tracking-tight">
                                        {totalPrice} €
                                    </p>
                                    <p className="text-sm text-gray-400 mt-1 mb-0">
                                        {quantity} × {unitPrice} € · {t("gift_per_year_access")}
                                    </p>
                                </div>

                                {/* Features */}
                                <div className="space-y-3">
                                    {[t("gift_features_1"), t("gift_features_2"), t("gift_features_3"), t("gift_features_4")].map((feature, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <SolarIcon name="unread-linear" className="w-4 h-4 text-green-500 flex-shrink-0" />
                                            <span className="text-sm text-gray-600 dark:text-grayDark-400">{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA */}
                                <button
                                    onClick={nextStep}
                                    className="w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-grayDark-900 rounded-xl font-medium hover:opacity-90 transition-opacity"
                                >
                                    {t("gift_continue")}
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 2: Recipient */}
                    {step === 2 && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <button
                                onClick={prevStep}
                                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white mb-8 transition-colors"
                            >
                                <SolarIcon name="arrow-left-linear" className="w-4 h-4" />
                                {t("gift_back")}
                            </button>

                            {quantity === 1 ? (
                                <div className="space-y-5">
                                    <p className="text-sm text-gray-500 dark:text-grayDark-400 text-center mb-6">
                                        {t("gift_recipient_subtitle")}
                                    </p>

                                    <div>
                                        <label className="block text-sm text-gray-600 dark:text-grayDark-400 mb-2">
                                            {t("gift_recipient_email")}
                                        </label>
                                        <input
                                            type="email"
                                            value={recipientEmail}
                                            onChange={(e) => setRecipientEmail(e.target.value)}
                                            placeholder={t("gift_recipient_email_placeholder")}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-grayDark-700 bg-transparent placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-gray-900 dark:focus:ring-white transition-shadow"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm text-gray-600 dark:text-grayDark-400 mb-2">
                                            {t("gift_recipient_firstName")}
                                        </label>
                                        <input
                                            type="text"
                                            value={recipientFirstName}
                                            onChange={(e) => setRecipientFirstName(e.target.value)}
                                            placeholder={t("gift_recipient_firstName_placeholder")}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-grayDark-700 bg-transparent placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-gray-900 dark:focus:ring-white transition-shadow"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm text-gray-600 dark:text-grayDark-400 mb-2">
                                            {t("gift_message")}
                                        </label>
                                        <textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder={t("gift_message_placeholder")}
                                            rows={3}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-grayDark-700 bg-transparent placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-gray-900 dark:focus:ring-white transition-shadow resize-none"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 dark:text-grayDark-400">
                                        {t("gift_multiple_codes_info")}
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={nextStep}
                                className="w-full mt-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-grayDark-900 rounded-xl font-medium hover:opacity-90 transition-opacity"
                            >
                                {t("gift_continue")}
                            </button>
                        </motion.div>
                    )}

                    {/* Step 3: Payment */}
                    {step === 3 && (
                        <motion.div
                            key="step3"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <button
                                onClick={prevStep}
                                disabled={loading}
                                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white mb-8 transition-colors disabled:opacity-50"
                            >
                                <SolarIcon name="arrow-left-linear" className="w-4 h-4" />
                                {t("gift_back")}
                            </button>

                            <div className="space-y-5">
                                <div>
                                    <label className="block text-sm text-gray-600 dark:text-grayDark-400 mb-2">
                                        {t("gift_buyer_name")} <span className="text-gray-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={buyerName}
                                        onChange={(e) => setBuyerName(e.target.value)}
                                        placeholder={t("gift_buyer_name_placeholder")}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-grayDark-700 bg-transparent placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-gray-900 dark:focus:ring-white transition-shadow"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm text-gray-600 dark:text-grayDark-400 mb-2">
                                        {t("gift_buyer_email")} <span className="text-gray-400">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={buyerEmail}
                                        onChange={(e) => setBuyerEmail(e.target.value)}
                                        placeholder={t("gift_buyer_email_placeholder")}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-grayDark-700 bg-transparent placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-gray-900 dark:focus:ring-white transition-shadow"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Summary */}
                            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-grayDark-800">
                                <div className="flex justify-between items-center mb-6">
                                    <span className="text-gray-600 dark:text-grayDark-400">{t("gift_total")}</span>
                                    <span className="text-2xl font-semibold text-gray-900 dark:text-white">{totalPrice} €</span>
                                </div>

                                <button
                                    onClick={handlePayment}
                                    disabled={loading}
                                    className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium btn-3d lip-indigo transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <SolarIcon name="refresh-linear" className="w-5 h-5 animate-spin" />
                                            {t("gift_pay_button_loading")}
                                        </>
                                    ) : (
                                        t("gift_pay_button_amount", { amount: totalPrice })
                                    )}
                                </button>

                                <p className="text-xs text-gray-400 text-center mt-4 mb-0">
                                    {t("gift_secure_payment")}
                                </p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Progress dots */}
                <div className="flex justify-center gap-1.5 mt-12">
                    {[1, 2, 3].map((s) => (
                        <div
                            key={s}
                            className={`rounded-full transition-all duration-300 ${
                                s === step
                                    ? "w-6 h-1.5 bg-gray-900 dark:bg-white"
                                    : "w-1.5 h-1.5 bg-gray-200 dark:bg-grayDark-700"
                            }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
